import {StructuredRemarksView} from './StructuredRemarksView'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  fetchAccessibleVesselsForCurrentUser,
  normalizeAccessibleVessels,
} from '../../../shared/access/accessibleVessels'
import {
  getAllInventoryCategoriesForCompany,
  getAllInventoryCategoriesForAllCompanies,
  getInventoryItemSubCategories,
  getGeneralInventoryItems,
} from '../../Procurement/core/_requests'
import type {
  InventoryCategory,
  InventoryItem,
  InventoryItemSubCategory,
} from '../../Procurement/core/_models'
import { useMonthEndFeatureAccess } from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'
import {
  ACCOUNTS_MONTH_END_API_URL,
  type AccountsMonthEndReport,
  type ResolvedAccountCode,
  type AvailableItem,
  type AllocationDto,
  type LineConsumptionDto,
  checkMonthTaken,
  companyOptionValue,
  extractApiError,
  fetchAccountCodesForItems,
  fetchAvailableItems,
  fetchLineAllocations,
  formatAmount,
  resolveItemName,
  sanitizeNumeric,
  toNumber,
} from '../core/accountsMonthEnd'

export type AccountsReportModalMode = 'add' | 'edit' | 'view'

type VesselOption = {
  id: string
  name: string
  companyAdminId: number | null
  companyGroupAdminId: number | null
}

type AccountsReportRow = {
  uid: string
  id?: string
  itemId: string
  itemName: string
  impaCode: string
  uom: string
  accountCodeId: string
  /** Derived from the item's purchase orders — displayed read-only, never picked by the user. */
  accountCode: string
  balanceCF: string
  consumption: string
  bunker: string
  initialCost: string
  rob: string
  consumptionValue?: string
  consumptionShortfallQty?: string
}

const makeUid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

/**
 * Every row renders its own set of searchable dropdowns, so seeding a row per inventory item
 * makes the option count grow quadratically and can exhaust the renderer. Cap the seed.
 */
const MAX_SEED_ROWS = 100

const emptyRow = (): AccountsReportRow => ({
  uid: makeUid(),
  itemId: '',
  itemName: '',
  impaCode: '',
  uom: '',
  accountCodeId: '',
  accountCode: '',
  balanceCF: '',
  consumption: '',
  bunker: '',
  initialCost: '',
  rob: '',
  consumptionValue: '',
  consumptionShortfallQty: '',
})

const getCurrentMonth = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Category and sub-category names repeat across companies, so collapse them to unique names for the
 * pickers. The surviving row's id is kept, which is why a saved id belongs to whichever company
 * appeared first in the catalogue.
 */
const dedupeByName = <T extends { id: number; name?: string }>(rows: T[]): T[] => {
  const seen = new Set<string>()
  return (rows || []).filter((row) => {
    const key = (row?.name || '').trim().toLowerCase()
    if (!row?.id || !key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * The inventory endpoint can hang when the backend connection pool is starved, and an unsettled
 * promise would leave the grid on "Loading..." forever. Cap the wait so a failure is visible.
 */
const INVENTORY_TIMEOUT_MS = 40000

const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('The server did not respond while loading inventory items')),
      ms
    )
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })

const AccountsMonthEndReportModal: FC<{
  isOpen: boolean
  mode: AccountsReportModalMode
  report?: AccountsMonthEndReport | null
  onClose: () => void
  onSaved: () => void
}> = ({ isOpen, mode, report, onClose, onSaved }) => {
  const { auth, currentUser, isSuperAdmin } = useAuth()
  const {
    canCreate,
    canUpdate,
    hasAssignedVessel,
    assignedVesselId,
    assignedCompanyGroupId,
    isGlobalScope,
  } = useMonthEndFeatureAccess('module1-report')
  const { companies } = useGlobalFilter()

  const isReadOnly = mode === 'view'
  const showCompanySelect = isSuperAdmin || isGlobalScope

  const [selectedCompanyKey, setSelectedCompanyKey] = useState('')
  const [selectedVesselId, setSelectedVesselId] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState('')
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState('')
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth())
  const [remarks, setRemarks] = useState('')

  const [vessels, setVessels] = useState<VesselOption[]>([])
  const [categories, setCategories] = useState<InventoryCategory[]>([])
  const [subCategories, setSubCategories] = useState<InventoryItemSubCategory[]>([])
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([])
  const [rows, setRows] = useState<AccountsReportRow[]>([emptyRow()])
  const [isLoadingItems, setIsLoadingItems] = useState(false)
  const [itemsError, setItemsError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  /** Set when the chosen vessel/month already has a report — blocks save and explains why. */
  const [monthTaken, setMonthTaken] = useState(false)

  const [breakdownLineId, setBreakdownLineId] = useState<number | null>(null)
  const [breakdownAllocations, setBreakdownAllocations] = useState<AllocationDto[]>([])
  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false)

  useEffect(() => {
    if (!breakdownLineId) {
      setBreakdownAllocations([])
      return
    }
    const loadBreakdown = async () => {
      setIsLoadingBreakdown(true)
      try {
        const res = await fetchLineAllocations(auth?.auth?.jwt || '', breakdownLineId)
        if (res) {
          setBreakdownAllocations(res.allocations || [])
        }
      } catch (err) {
        toast.error('Failed to load stock allocation breakdown')
      } finally {
        setIsLoadingBreakdown(false)
      }
    }
    loadBreakdown()
  }, [breakdownLineId, auth?.auth?.jwt])

  const selectedCompany = useMemo(
    () => companies.find((company) => companyOptionValue(company) === selectedCompanyKey) || null,
    [companies, selectedCompanyKey]
  )

  /**
   * Requisitions only surface Sub Category for store-type categories, and even then it is optional.
   */
  const selectedCategory = useMemo(
    () => categories.find((category) => String(category.id) === selectedCategoryId) || null,
    [categories, selectedCategoryId]
  )
  const isStoreCategory = (selectedCategory?.name || '').toUpperCase().includes('STORE')

  /** Sub-category names repeat across companies, so collapse duplicates as the requisition form does. */
  const subCategoryOptions = useMemo(() => dedupeByName(subCategories), [subCategories])

  /* ── Reset / hydrate whenever the modal opens ── */
  useEffect(() => {
    if (!isOpen) return
    setErrors({})

    const populateFromReport = (rep: AccountsMonthEndReport) => {
      setSelectedVesselId(rep.vesselId != null ? String(rep.vesselId) : '')
      setSelectedMonth(
        rep.reportYear && rep.reportMonth
          ? `${rep.reportYear}-${String(rep.reportMonth).padStart(2, '0')}`
          : getCurrentMonth()
      )
      setRemarks(rep.remarks || '')
      setSelectedCompanyKey(
        rep.companyId && rep.companyType ? `${rep.companyType}:${rep.companyId}` : ''
      )
      const firstCategory = (rep.lines || []).find(
        (line) => line.inventoryItemCategoryId != null
      )?.inventoryItemCategoryId
      setSelectedCategoryId(firstCategory != null ? String(firstCategory) : '')
      const firstSubCategory = (rep.lines || []).find(
        (line) => line.inventoryItemSubCategoryId != null
      )?.inventoryItemSubCategoryId
      setSelectedSubCategoryId(firstSubCategory != null ? String(firstSubCategory) : '')
      setRows(
        (rep.lines || []).length > 0
          ? (rep.lines || []).map((line) => ({
            uid: makeUid(),
            id: line.id != null ? String(line.id) : undefined,
            itemId: line.inventoryItemId != null ? String(line.inventoryItemId) : '',
            itemName: line.itemName || '',
            impaCode: line.impaCode || '',
            uom: line.unitOfMeasurement || '',
            accountCodeId: line.accountCodeId != null ? String(line.accountCodeId) : '',
            accountCode: line.accountCode || '',
            balanceCF:
              line.balanceCarriedForward != null ? String(line.balanceCarriedForward) : '',
            consumption: line.consumption != null ? String(line.consumption) : '',
            bunker: line.bunker != null ? String(line.bunker) : '',
            initialCost: line.initialCost != null ? String(line.initialCost) : '',
            rob: line.rob != null ? String(line.rob) : '',
            consumptionValue: line.consumptionValue != null ? String(line.consumptionValue) : '',
            consumptionShortfallQty: line.consumptionShortfallQty != null ? String(line.consumptionShortfallQty) : '',
          }))
          : isReadOnly
            ? []
            : [emptyRow()]
      )
    }

    if (report) {
      populateFromReport(report)
      if (report.id && (!report.lines || report.lines.length === 0) && auth?.auth?.jwt) {
        fetch(`${ACCOUNTS_MONTH_END_API_URL}/${report.id}`, {
          headers: { Authorization: `Bearer ${auth.auth.jwt}` },
        })
          .then((res) => (res.ok ? res.json() : null))
          .then((fullReport) => {
            if (fullReport) populateFromReport(fullReport)
          })
          .catch(() => { })
      }
      return
    }

    setSelectedCompanyKey('')
    setSelectedVesselId(hasAssignedVessel && assignedVesselId ? String(assignedVesselId) : '')
    setSelectedCategoryId('')
    setSelectedSubCategoryId('')
    setSelectedMonth(getCurrentMonth())
    setRemarks('')
    setRows([emptyRow()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, report, isReadOnly])

  /* ── Vessels: backend-scoped list, narrowed further by the chosen company ── */
  useEffect(() => {
    if (!isOpen) return

    if (hasAssignedVessel && currentUser?.vessel) {
      const vessel = currentUser.vessel as any
      setVessels([
        {
          id: String(vessel.id),
          name: vessel.fleet_name || vessel.name || `Vessel #${vessel.id}`,
          companyAdminId: vessel.companyAdmin?.id ?? null,
          companyGroupAdminId: vessel.companyGroupAdmin?.id ?? null,
        },
      ])
      return
    }

    let cancelled = false
    const loadVessels = async () => {
      try {
        const list = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
        if (cancelled) return
        setVessels(
          list.map((vessel: any) => ({
            id: String(vessel.id),
            name: vessel.fleet_name || vessel.name || `Vessel #${vessel.id}`,
            companyAdminId: vessel.companyAdmin?.id ?? null,
            companyGroupAdminId: vessel.companyGroupAdmin?.id ?? null,
          }))
        )
      } catch (error) {
        if (!cancelled) setVessels([])
      }
    }
    loadVessels()
    return () => {
      cancelled = true
    }
  }, [isOpen, hasAssignedVessel, currentUser])

  const vesselOptions = useMemo(() => {
    if (!selectedCompany) return vessels
    return vessels.filter((vessel) =>
      selectedCompany.type === 'company_group'
        ? vessel.companyGroupAdminId === selectedCompany.id
        : vessel.companyAdminId === selectedCompany.id
    )
  }, [vessels, selectedCompany])

  const selectedVessel = useMemo(
    () => vessels.find((vessel) => vessel.id === selectedVesselId) || null,
    [vessels, selectedVesselId]
  )

  /* ── Categories: scoped to the resolved company group when we have one ── */
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const loadCategories = async () => {
      const cgaId =
        assignedCompanyGroupId ??
        (selectedCompany?.type === 'company_group' ? selectedCompany.id : null) ??
        selectedVessel?.companyGroupAdminId ??
        null
      try {
        /*
         * Global-scope users choose from every company's catalogue — a deliberate divergence from
         * requisitions, which narrow to the selected company. Others stay scoped to their own group.
         */
        const list =
          showCompanySelect || !cgaId
            ? await getAllInventoryCategoriesForAllCompanies()
            : await getAllInventoryCategoriesForCompany(cgaId)
        if (cancelled) return
        setCategories(dedupeByName(list || []))
      } catch (error) {
        if (!cancelled) setCategories([])
      }
    }
    loadCategories()
    return () => {
      cancelled = true
    }
  }, [isOpen, showCompanySelect, assignedCompanyGroupId, selectedCompany, selectedVessel])

  /* ── Sub-categories are a flat active-only master list, exactly as the requisition form loads them ── */
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const loadSubCategories = async () => {
      try {
        const list = await getInventoryItemSubCategories(true)
        if (!cancelled) setSubCategories(Array.isArray(list) ? list : [])
      } catch (error) {
        if (!cancelled) setSubCategories([])
      }
    }
    loadSubCategories()
    return () => {
      cancelled = true
    }
  }, [isOpen])

  /* ── One report per vessel per month: flag the clash as soon as the month is picked ── */
  useEffect(() => {
    if (!isOpen || isReadOnly || !selectedVesselId || !selectedMonth || !auth?.auth?.jwt) {
      setMonthTaken(false)
      return
    }
    const [year, month] = selectedMonth.split('-')
    if (!year || !month) {
      setMonthTaken(false)
      return
    }

    let cancelled = false
    const check = async () => {
      try {
        const taken = await checkMonthTaken(
          auth.auth!.jwt,
          selectedVesselId,
          Number(year),
          Number(month),
          // An edit must not collide with the row being edited.
          mode === 'edit' && report?.id ? report.id : null
        )
        if (cancelled) return
        setMonthTaken(taken)
        setErrors((prev) => {
          const next = { ...prev }
          if (taken) {
            next.month = 'A report already exists for this vessel and month'
          } else if (next.month === 'A report already exists for this vessel and month') {
            delete next.month
          }
          return next
        })
      } catch (error) {
        // A failed check must not block the user — the backend still rejects a true duplicate.
        if (!cancelled) setMonthTaken(false)
      }
    }
    check()
    return () => {
      cancelled = true
    }
  }, [isOpen, isReadOnly, selectedVesselId, selectedMonth, mode, report, auth?.auth?.jwt])

  /* ── Account codes come from the item's purchase orders, never from user choice ── */
  useEffect(() => {
    if (!isOpen || isReadOnly || !selectedVesselId || !auth?.auth?.jwt) return

    const itemIds = Array.from(
      new Set(rows.map((row) => row.itemId).filter((id): id is string => Boolean(id)))
    )
    // Only ask for codes we do not already hold, so typing in a cost cell cannot trigger a refetch.
    const missing = itemIds.filter(
      (id) => !rows.some((row) => row.itemId === id && row.accountCode)
    )
    if (missing.length === 0) return

    let cancelled = false
    const resolve = async () => {
      try {
        const resolved = await fetchAccountCodesForItems(auth.auth!.jwt, selectedVesselId, missing)
        if (cancelled || Object.keys(resolved).length === 0) return
        setRows((prev) =>
          prev.map((row) => {
            const hit: ResolvedAccountCode | undefined = row.itemId
              ? resolved[String(row.itemId)]
              : undefined
            if (!hit) return row
            return {
              ...row,
              accountCodeId: hit.accountCodeId != null ? String(hit.accountCodeId) : '',
              accountCode: hit.accountCode || '',
            }
          })
        )
      } catch (error) {
        // Leave the cell blank — the backend re-derives the code on save regardless.
      }
    }
    resolve()
    return () => {
      cancelled = true
    }
  }, [isOpen, isReadOnly, selectedVesselId, rows, auth?.auth?.jwt])

  /* ── Available Items: retrieve only inventory items with stock on board ── */
  const loadInventory = async (
    vesselId: string,
    subCategoryId: string,
    options?: { silent?: boolean }
  ) => {
    if (!vesselId) {
      setInventoryItems([])
      setItemsError('')
      return
    }

    setIsLoadingItems(true)
    setItemsError('')
    try {
      const items = await fetchAvailableItems(auth?.auth?.jwt || '', vesselId, subCategoryId)
      const list = Array.isArray(items) ? items : []
      setInventoryItems(list as any)
      if (list.length === 0 && !options?.silent) {
        toast.info('No inventory items with stock found for this selection')
      }
    } catch (error: any) {
      setInventoryItems([])
      setItemsError(error?.message || 'Failed to load vessel inventory')
      if (!options?.silent) toast.error(error?.message || 'Failed to load vessel inventory')
    } finally {
      setIsLoadingItems(false)
    }
  }

  useEffect(() => {
    if (!isOpen || isReadOnly) return
    loadInventory(selectedVesselId, selectedSubCategoryId, { silent: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedVesselId, selectedSubCategoryId, isReadOnly])

  const computeRob = (balanceCF: string, bunker: string, consumption: string) => {
    const hasValue = balanceCF !== '' || bunker !== '' || consumption !== ''
    if (!hasValue) return ''
    const b = toNumber(balanceCF)
    const bk = toNumber(bunker)
    const c = toNumber(consumption)
    const result = b + bk - c
    return Number.isFinite(result) ? String(parseFloat(result.toFixed(4))) : '0'
  }

  const seedRowsFromInventory = () => {
    if (inventoryItems.length === 0) {
      toast.info('No inventory items to load for this selection')
      return
    }

    setRows(
      inventoryItems.map((item: any) => {
        const bal = item.quantity != null ? String(item.quantity) : ''
        return {
          ...emptyRow(),
          itemId: String(item.id),
          itemName: item.itemName || `Item #${item.id}`,
          impaCode: item.impaCode || '',
          uom: item.unitOfMeasurement || '',
          balanceCF: bal,
          initialCost: item.weightedPrice != null ? String(item.weightedPrice) : '',
          rob: computeRob(bal, '', ''),
        }
      })
    )
  }

  const setRowField = (uid: string, patch: Partial<AccountsReportRow>) =>
    setRows((prev) =>
      prev.map((row) => {
        if (row.uid !== uid) return row
        const nextRow = { ...row, ...patch }
        if ('balanceCF' in patch || 'bunker' in patch || 'consumption' in patch) {
          nextRow.rob = computeRob(nextRow.balanceCF, nextRow.bunker, nextRow.consumption)
        }
        return nextRow
      })
    )

  const handleItemChange = (uid: string, itemId: string) => {
    const item: any = inventoryItems.find((entry) => String(entry.id) === itemId)
    if (!item) {
      setRowField(uid, {
        itemId: '',
        itemName: '',
        impaCode: '',
        uom: '',
        accountCodeId: '',
        accountCode: '',
        balanceCF: '',
        consumption: '',
        bunker: '',
        initialCost: '',
        rob: '',
      })
      return
    }
    const bal = item.quantity != null ? String(item.quantity) : ''
    setRows((prev) =>
      prev.map((row) => {
        if (row.uid !== uid) return row
        return {
          ...row,
          itemId,
          itemName: item.itemName || `Item #${item.id}`,
          impaCode: item.impaCode || '',
          uom: item.unitOfMeasurement || '',
          accountCodeId: '',
          accountCode: '',
          balanceCF: bal,
          initialCost: item.weightedPrice != null ? String(item.weightedPrice) : '',
          rob: computeRob(bal, row.bunker, row.consumption),
        }
      })
    )
  }

  const addRow = () => setRows((prev) => [...prev, emptyRow()])

  const removeRow = (uid: string) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((row) => row.uid !== uid) : prev))

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          balanceCF: acc.balanceCF + toNumber(row.balanceCF),
          consumption: acc.consumption + toNumber(row.consumption),
          bunker: acc.bunker + toNumber(row.bunker),
          initialCost: acc.initialCost + toNumber(row.initialCost),
          rob: acc.rob + toNumber(row.rob),
          consumptionValue:
            acc.consumptionValue + (row.consumptionValue ? toNumber(row.consumptionValue) : 0),
        }),
        { balanceCF: 0, consumption: 0, bunker: 0, initialCost: 0, rob: 0, consumptionValue: 0 }
      ),
    [rows]
  )

  const filledRows = useMemo(() => rows.filter((row) => row.itemId), [rows])

  const validate = () => {
    const nextErrors: Record<string, string> = {}
    if (showCompanySelect && !selectedCompanyKey) nextErrors.company = 'Company is required'
    if (!selectedVesselId) nextErrors.vessel = 'Vessel is required'
    if (!selectedMonth) nextErrors.month = 'Month is required'
    else if (monthTaken)
      nextErrors.month = 'A report already exists for this vessel and month'
    if (filledRows.length === 0) nextErrors.rows = 'Add at least one item row'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const [year, month] = selectedMonth.split('-')
    const payload = {
      companyId: selectedCompany?.id ?? null,
      companyType: selectedCompany?.type ?? null,
      vesselId: Number(selectedVesselId),
      reportYear: Number(year),
      reportMonth: Number(month),
      remarks: remarks.trim() || null,
      lines: filledRows.map((row) => ({
        inventoryItemCategoryId: selectedCategoryId ? Number(selectedCategoryId) : null,
        inventoryItemSubCategoryId: selectedSubCategoryId ? Number(selectedSubCategoryId) : null,
        inventoryItemId: Number(row.itemId),
        itemName: row.itemName,
        impaCode: row.impaCode || null,
        unitOfMeasurement: row.uom || null,
        accountCodeId: row.accountCodeId ? Number(row.accountCodeId) : null,
        accountCode: row.accountCode || null,
        balanceCarriedForward: toNumber(row.balanceCF),
        consumption: toNumber(row.consumption),
        bunker: toNumber(row.bunker),
        initialCost: toNumber(row.initialCost),
        rob: toNumber(row.rob),
      })),
    }

    setIsSubmitting(true)
    try {
      const isEdit = mode === 'edit' && report?.id
      const response = await fetch(
        isEdit ? `${ACCOUNTS_MONTH_END_API_URL}/${report!.id}` : ACCOUNTS_MONTH_END_API_URL,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.auth?.jwt}`,
          },
          body: JSON.stringify(payload),
        }
      )
      if (!response.ok) {
        const message = await extractApiError(
          response,
          'Failed to save Accounts Month End Report'
        )
        // The duplicate-month rule is enforced server-side too, so pin it to the month field.
        if (response.status === 400 && /already exists/i.test(message)) {
          setMonthTaken(true)
          setErrors((prev) => ({ ...prev, month: message }))
        }
        throw new Error(message)
      }
      toast.success(isEdit ? 'Report updated' : 'Report created')
      onSaved()
      onClose()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save Accounts Month End Report')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const permitted = isReadOnly || (mode === 'edit' ? canUpdate : canCreate)
  const title =
    mode === 'add'
      ? 'Add Accounts Month End Report'
      : mode === 'edit'
        ? 'Edit Accounts Month End Report'
        : 'Accounts Month End Report'

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}
    >
      <div className='modal-dialog modal-dialog-centered modal-xl'>
        <div className='modal-content'>
          <form onSubmit={handleSubmit}>
            <div className='modal-header'>
              <h5 className='modal-title'>
                <KTSVG path='/media/icons/duotune/finance/fin008.svg' className='svg-icon-3 me-3' />
                {title}
              </h5>
              <button
                type='button'
                className='btn-close'
                onClick={onClose}
                disabled={isSubmitting}
              />
            </div>

            {!permitted ? (
              <div className='modal-body'>
                <QhseAccessDenied message='Access denied — you do not have permission to manage Month End Reports.' />
              </div>
            ) : (
              <>
                <div className='modal-body'>
                  {/* ── Scope ── */}
                  <div className='row g-5 mb-8'>
                    {showCompanySelect && (
                      <div className='col-md-4'>
                        <label className='required fw-semibold fs-6 mb-2'>Company</label>
                        <SearchableSelect
                          className={`form-select form-select-solid ${errors.company ? 'is-invalid' : ''}`}
                          value={selectedCompanyKey}
                          disabled={isReadOnly || isSubmitting}
                          onChange={(e) => {
                            setSelectedCompanyKey(e.target.value)
                            setSelectedVesselId('')
                          }}
                        >
                          <option value=''>Select Company</option>
                          {companies.map((company) => (
                            <option
                              key={companyOptionValue(company)}
                              value={companyOptionValue(company)}
                            >
                              {company.name}
                            </option>
                          ))}
                        </SearchableSelect>
                        {errors.company && (
                          <div className='text-danger fs-7 mt-1'>{errors.company}</div>
                        )}
                      </div>
                    )}

                    <div className='col-md-4'>
                      <label className='required fw-semibold fs-6 mb-2'>Vessel</label>
                      {hasAssignedVessel || isReadOnly ? (
                        <div
                          className='form-control form-control-solid bg-light text-muted'
                          style={{ cursor: 'not-allowed' }}
                        >
                          {selectedVessel?.name || report?.vesselName || 'My Vessel'}
                        </div>
                      ) : (
                        <SearchableSelect
                          className={`form-select form-select-solid ${errors.vessel ? 'is-invalid' : ''}`}
                          value={selectedVesselId}
                          disabled={isSubmitting}
                          onChange={(e) => setSelectedVesselId(e.target.value)}
                        >
                          <option value=''>Select Vessel</option>
                          {vesselOptions.map((vessel) => (
                            <option key={vessel.id} value={vessel.id}>
                              {vessel.name}
                            </option>
                          ))}
                        </SearchableSelect>
                      )}
                      {errors.vessel && <div className='text-danger fs-7 mt-1'>{errors.vessel}</div>}
                    </div>

                    <div className='col-md-4'>
                      <label className='required fw-semibold fs-6 mb-2'>Month</label>
                      <input
                        type='month'
                        className={`form-control form-control-solid ${errors.month ? 'is-invalid' : ''}`}
                        value={selectedMonth}
                        disabled={isReadOnly || isSubmitting}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      />
                      {errors.month && <div className='text-danger fs-7 mt-1'>{errors.month}</div>}
                    </div>

                    <div className='col-md-4'>
                      <label className='fw-semibold fs-6 mb-2'>Category</label>
                      <SearchableSelect
                        className='form-select form-select-solid'
                        value={selectedCategoryId}
                        disabled={isReadOnly || isSubmitting}
                        onChange={(e) => {
                          setSelectedCategoryId(e.target.value)
                          // Sub Category only exists for store categories, so never leave a hidden filter behind.
                          setSelectedSubCategoryId('')
                        }}
                      >
                        <option value=''>Select Category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={String(category.id)}>
                            {category.name}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>

                    {isStoreCategory && (
                      <div className='col-md-4'>
                        <label className='fw-semibold fs-6 mb-2'>Sub Category (optional)</label>
                        <SearchableSelect
                          className='form-select form-select-solid'
                          value={selectedSubCategoryId}
                          disabled={isReadOnly || isSubmitting}
                          onChange={(e) => setSelectedSubCategoryId(e.target.value)}
                        >
                          <option value=''>Select Sub Category (optional)</option>
                          {subCategoryOptions.map((subCategory) => (
                            <option key={subCategory.id} value={String(subCategory.id)}>
                              {subCategory.name}
                            </option>
                          ))}
                        </SearchableSelect>
                        <span className='text-muted fs-8 d-block mt-1'>
                          Narrows the item list below
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ── Line items ── */}
                  <div className='d-flex align-items-center justify-content-between mb-3'>
                    <div>
                      <span className='fw-bold fs-5 text-dark d-block'>
                        Consumption &amp; Cost Statement
                      </span>
                      <span className='text-muted fw-semibold fs-7'>
                        {isReadOnly
                          ? 'Saved line items for this report'
                          : !selectedVesselId
                            ? 'Select a vessel to load its inventory items'
                            : itemsError
                              ? 'Inventory items could not be loaded'
                              : `${inventoryItems.length} item${inventoryItems.length === 1 ? '' : 's'} available for this vessel`}
                      </span>
                    </div>
                    {!isReadOnly && (
                      <div className='d-flex align-items-center gap-2'>
                        <button
                          type='button'
                          className='btn btn-light-primary btn-sm'
                          onClick={seedRowsFromInventory}
                          disabled={!selectedVesselId || isLoadingItems || isSubmitting}
                        >
                          <KTSVG
                            path='/media/icons/duotune/arrows/arr029.svg'
                            className='svg-icon-4 me-2'
                          />
                          Load Items from Inventory
                        </button>
                        <button
                          type='button'
                          className='btn btn-primary btn-sm'
                          onClick={addRow}
                          disabled={isSubmitting}
                        >
                          <KTSVG
                            path='/media/icons/duotune/arrows/arr075.svg'
                            className='svg-icon-4 me-2'
                          />
                          Add Row
                        </button>
                      </div>
                    )}
                  </div>

                  {errors.rows && <div className='text-danger fs-7 mb-2'>{errors.rows}</div>}

                  <div className='table-responsive'>
                    <table className='table table-bordered align-middle mb-0'>
                      <thead className='table-header'>
                        <tr className='fw-bold text-muted'>
                          <th className='text-center' style={{ width: 55 }}>
                            SR
                          </th>
                          <th style={{ minWidth: 240 }}>Name of Item (IMPA)</th>
                          <th style={{ minWidth: 160 }}>Account Code</th>
                          <th style={{ minWidth: 120 }}>Balance C/F</th>
                          <th style={{ minWidth: 120 }}>Consumption</th>
                          <th style={{ minWidth: 120 }}>Bunker</th>
                          <th style={{ minWidth: 120 }}>Initial Cost</th>
                          <th style={{ minWidth: 120 }}>ROB</th>
                          {!isReadOnly && (
                            <th className='text-center' style={{ width: 65 }}>
                              Action
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {!isReadOnly && isLoadingItems ? (
                          <tr>
                            <td colSpan={9} className='text-center text-muted py-10'>
                              Loading vessel inventory...
                            </td>
                          </tr>
                        ) : !isReadOnly && itemsError ? (
                          <tr>
                            <td colSpan={9} className='text-center py-10'>
                              <span className='text-danger fw-semibold d-block mb-3'>
                                {itemsError}
                              </span>
                              <button
                                type='button'
                                className='btn btn-sm btn-light-primary'
                                onClick={() =>
                                  loadInventory(selectedVesselId, selectedSubCategoryId)
                                }
                              >
                                Retry
                              </button>
                            </td>
                          </tr>
                        ) : rows.length === 0 || (isReadOnly && rows.every((r) => !r.itemName && !r.itemId)) ? (
                          <tr>
                            <td colSpan={isReadOnly ? 8 : 9} className='text-center text-muted py-10'>
                              No line items found for this report.
                            </td>
                          </tr>
                        ) : (
                          rows.map((row, index) => (
                            <tr key={row.uid}>
                              <td className='text-center text-muted'>{index + 1}</td>
                              <td>
                                {isReadOnly ? (
                                  <span className='text-dark'>
                                    {row.impaCode
                                      ? `${row.itemName} (${row.impaCode})`
                                      : row.itemName || '-'}
                                  </span>
                                ) : (
                                  <SearchableSelect
                                    className='form-select form-select-sm'
                                    value={row.itemId}
                                    disabled={!selectedVesselId || isSubmitting}
                                    onChange={(e) => handleItemChange(row.uid, e.target.value)}
                                  >
                                    <option value=''>Select Item</option>
                                    {inventoryItems.map((item: any) => (
                                      <option key={item.id} value={String(item.id)}>
                                        {item.impaCode
                                          ? `${resolveItemName(item)} (${item.impaCode})`
                                          : resolveItemName(item)}
                                      </option>
                                    ))}
                                  </SearchableSelect>
                                )}
                                {row.uom && (
                                  <span className='text-muted fs-8 d-block mt-1'>
                                    UOM: {row.uom}
                                  </span>
                                )}
                              </td>
                              <td>
                                {/*
                                  Account code is derived from the item's purchase orders and is
                                  read-only in every mode — the backend re-derives it on save, so an
                                  editable control here would be misleading.
                                */}
                                {row.accountCode ? (
                                  <span className='text-dark'>{row.accountCode}</span>
                                ) : row.itemId ? (
                                  <span
                                    className='text-muted fs-8'
                                    title='No purchase order for this item carries an account code'
                                  >
                                    Not mapped
                                  </span>
                                ) : (
                                  <span className='text-muted'>-</span>
                                )}
                              </td>
                              {(
                                [
                                  'balanceCF',
                                  'consumption',
                                  'bunker',
                                  'initialCost',
                                  'rob',
                                ] as const
                              ).map((field) => (
                                <td key={field}>
                                  {isReadOnly ? (
                                    <div className='text-end text-dark'>
                                      {row[field] === '' ? '-' : formatAmount(toNumber(row[field]))}
                                    </div>
                                  ) : field === 'rob' ? (
                                    <input
                                      type='text'
                                      className='form-control form-control-sm form-control-solid text-end bg-light text-dark fw-semibold'
                                      value={row.rob !== '' ? formatAmount(toNumber(row.rob)) : '-'}
                                      readOnly
                                      tabIndex={-1}
                                      title='Auto-calculated: (Balance C/F + Bunker) - Consumption'
                                    />
                                  ) : (
                                    <input
                                      type='text'
                                      inputMode='decimal'
                                      className='form-control form-control-sm text-end'
                                      value={row[field]}
                                      disabled={isSubmitting}
                                      onChange={(e) =>
                                        setRowField(row.uid, {
                                          [field]: sanitizeNumeric(e.target.value),
                                        })
                                      }
                                    />
                                  )}
                                  {field === 'consumption' && (
                                    <>
                                      {row.id && row.consumptionValue && toNumber(row.consumptionValue) > 0 && (
                                        <div
                                          className='text-primary fs-8 mt-1 text-end'
                                          onClick={() => setBreakdownLineId(Number(row.id))}
                                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                          title="Click to view FIFO cost breakdown"
                                        >
                                          Value: ${formatAmount(toNumber(row.consumptionValue))}
                                        </div>
                                      )}
                                      {!row.id && row.consumptionValue && toNumber(row.consumptionValue) > 0 && (
                                        <div className='text-muted fs-8 mt-1 text-end'>
                                          Value: ${formatAmount(toNumber(row.consumptionValue))}
                                        </div>
                                      )}
                                      {row.consumptionShortfallQty && toNumber(row.consumptionShortfallQty) > 0 && (
                                        <div
                                          className='text-danger fs-8 mt-1 fw-bold text-end'
                                          title="Consumption exceeds available stock on hand"
                                        >
                                          ⚠️ Shortfall: {formatAmount(toNumber(row.consumptionShortfallQty))}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </td>
                              ))}
                              {!isReadOnly && (
                                <td className='text-center'>
                                  <button
                                    type='button'
                                    className='btn btn-icon btn-sm'
                                    title='Remove row'
                                    onClick={() => removeRow(row.uid)}
                                    disabled={rows.length === 1 || isSubmitting}
                                  >
                                    <KTSVG
                                      path='/media/map/red-cross-icon.svg'
                                      className='svg-icon-3'
                                    />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                      {!isLoadingItems && !(itemsError && !isReadOnly) && (
                        <tfoot>
                          <tr className='fw-bold text-dark'>
                            <td colSpan={3} className='text-end'>
                              Total
                            </td>
                            <td className='text-end'>{formatAmount(totals.balanceCF)}</td>
                            <td className='text-end'>
                              <div>{formatAmount(totals.consumption)}</div>
                              {totals.consumptionValue > 0 && (
                                <div className='text-primary fs-8 mt-1' title='Total FIFO consumption value'>
                                  Total: ${formatAmount(totals.consumptionValue)}
                                </div>
                              )}
                            </td>
                            <td className='text-end'>{formatAmount(totals.bunker)}</td>
                            <td className='text-end'>{formatAmount(totals.initialCost)}</td>
                            <td className='text-end'>{formatAmount(totals.rob)}</td>
                            {!isReadOnly && <td></td>}
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>

                  <div className='mt-8'>
                    <label className='fw-semibold fs-6 mb-2'>Remarks</label>
                    {isReadOnly ? (
                      <StructuredRemarksView text={remarks} />
                    ) : (
                      <textarea
                        className='form-control form-control-solid'
                        rows={2}
                        value={remarks}
                        disabled={isSubmitting}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder='Optional notes for this month end statement'
                      />
                    )}
                  </div>
                </div>

                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    {isReadOnly ? 'Close' : 'Cancel'}
                  </button>
                  {!isReadOnly && (
                    <button
                      type='submit'
                      className='btn btn-primary'
                      disabled={isSubmitting || monthTaken}
                      title={
                        monthTaken
                          ? 'A report already exists for this vessel and month'
                          : undefined
                      }
                    >
                      {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update Report' : 'Save Report'}
                    </button>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </div>
      {breakdownLineId && (
        <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex={-1}>
          <div className='modal-dialog modal-lg modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>FIFO Consumption Breakdown</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setBreakdownLineId(null)}
                ></button>
              </div>
              <div className='modal-body'>
                {isLoadingBreakdown ? (
                  <div className='text-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : breakdownAllocations.length === 0 ? (
                  <div className='text-center text-muted py-10'>No allocations recorded.</div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table table-bordered align-middle'>
                      <thead>
                        <tr className='fw-bold text-muted bg-light'>
                          <th>PO Number</th>
                          <th>Received Date</th>
                          <th className='text-end'>Qty Drawn</th>
                          <th className='text-end'>Unit Cost</th>
                          <th className='text-end'>Line Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdownAllocations.map((alloc) => (
                          <tr key={alloc.id}>
                            <td>{alloc.poNumber || '-'}</td>
                            <td>{alloc.lotReceivedDate || '-'}</td>
                            <td className='text-end'>{alloc.allocatedQty != null ? formatAmount(alloc.allocatedQty) : '-'}</td>
                            <td className='text-end'>{alloc.unitCost != null ? `$${formatAmount(alloc.unitCost)}` : '-'}</td>
                            <td className='text-end'>{alloc.allocatedValue != null ? `$${formatAmount(alloc.allocatedValue)}` : '-'}</td>
                          </tr>
                        ))}
                        <tr className='fw-bold bg-light'>
                          <td colSpan={2}>Total</td>
                          <td className='text-end'>
                            {formatAmount(breakdownAllocations.reduce((sum, a) => sum + (a.allocatedQty || 0), 0))}
                          </td>
                          <td></td>
                          <td className='text-end'>
                            ${formatAmount(breakdownAllocations.reduce((sum, a) => sum + (a.allocatedValue || 0), 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setBreakdownLineId(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountsMonthEndReportModal
